import classNames from 'classnames';
import type { MouseEvent, ReactNode } from 'react';
import type { CalendarViewMode } from './dateUtils';
import { toISODate } from './dateUtils';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export interface CalendarDayRenderProps {
  date: Date;
  inCurrentMonth: boolean;
  isToday: boolean;
  isoDate: string;
  viewMode: CalendarViewMode;
}

export interface CalendarMonthWeekGridProps {
  anchor: Date;
  dates: Date[];
  dataCyDayPrefix?: string;
  disabledDates?: ReadonlySet<string>;
  getDateAriaLabel?: (day: CalendarDayRenderProps) => string;
  onDateClick?: (isoDate: string, options: { shiftKey: boolean }) => void;
  renderDay: (day: CalendarDayRenderProps) => ReactNode;
  selectedDates?: ReadonlySet<string>;
  today?: Date;
  viewMode: CalendarViewMode;
}

export const CalendarMonthWeekGrid = ({
  anchor,
  dates,
  dataCyDayPrefix = 'calendar-day',
  disabledDates = new Set<string>(),
  getDateAriaLabel,
  onDateClick,
  renderDay,
  selectedDates = new Set<string>(),
  today = new Date(),
  viewMode,
}: CalendarMonthWeekGridProps) => {
  const isMonth = viewMode === 'month';
  const todayISO = toISODate(today);

  return (
    <div className="overflow-hidden rounded-xl border border-grey-900 bg-white shadow-sm">
      <div className="grid grid-cols-7 border-b border-grey-900 bg-grey-950">
        {WEEKDAYS.map((weekday) => (
          <div className="heading-sb px-2 py-2 uppercase text-grey-500" key={weekday}>
            {weekday}
          </div>
        ))}
      </div>
      <div className={classNames('grid grid-cols-7', isMonth ? 'grid-rows-6' : 'grid-rows-1')}>
        {dates.map((date) => {
          const isoDate = toISODate(date);
          const dayProps: CalendarDayRenderProps = {
            date,
            inCurrentMonth: date.getMonth() === anchor.getMonth(),
            isToday: isoDate === todayISO,
            isoDate,
            viewMode,
          };
          const selected = selectedDates.has(isoDate);
          const disabled = disabledDates.has(isoDate);
          // Native buttons otherwise vertically center the date and its entries.
          const cellClassName = classNames(
            'flex min-w-0 flex-col border-b border-r border-grey-900 text-left',
            isMonth ? 'min-h-24' : 'min-h-96',
            isMonth && !dayProps.inCurrentMonth && 'bg-grey-950/60'
          );
          const contents = (
            <>
              <span
                className={classNames(
                  'mb-1 inline-flex size-6 shrink-0 self-start items-center justify-center rounded-full text-xs',
                  dayProps.isToday ? 'bg-primary-50 font-bold text-white' : 'text-grey-400'
                )}
              >
                {date.getDate()}
              </span>
              {renderDay(dayProps)}
            </>
          );

          return onDateClick ? (
            <button
              aria-current={dayProps.isToday ? 'date' : undefined}
              aria-label={
                getDateAriaLabel?.(dayProps) ??
                date.toLocaleDateString(undefined, {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })
              }
              aria-pressed={selected}
              className={classNames(
                cellClassName,
                'w-full p-1 transition focus-visible:z-10 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-inset focus-visible:ring-primary-50/30',
                selected && 'bg-primary-50/10 ring-2 ring-inset ring-primary-50',
                disabled && 'cursor-not-allowed opacity-60'
              )}
              data-cy={`${dataCyDayPrefix}-${isoDate}`}
              data-calendar-date={isoDate}
              disabled={disabled}
              key={isoDate}
              onClick={(event: MouseEvent<HTMLButtonElement>) =>
                onDateClick(isoDate, { shiftKey: event.shiftKey })
              }
              type="button"
            >
              {contents}
            </button>
          ) : (
            <div
              className={classNames(cellClassName, 'p-1')}
              data-cy={`${dataCyDayPrefix}-${isoDate}`}
              data-calendar-date={isoDate}
              key={isoDate}
            >
              {contents}
            </div>
          );
        })}
      </div>
    </div>
  );
};
